import { useId } from "react";
import type { FC } from "react";
import classnames from "classnames";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";

import type { ImageObject } from "@motain/xpa-proto-files-web/lib/types/image";
import { TeamSide } from "@motain/xpa-proto-files-web/lib/types/team_side";
import type { TrackingEvent } from "@motain/xpa-proto-files-web/lib/types/tracking";
import { EventType } from "@motain/xpa-proto-files-web/lib/types/tracking";
import { EntityLogo } from "@/components/entity-logo/EntityLogo";
import { trackingService } from "@/modules/analytics";
import { isNonEmptyString } from "@/utils/type-guards/isNonEmptyStringGuard";

import styles from "./MatchScoreTeam.module.scss";

interface MatchScoreTeamProps {
  name: string;
  side: TeamSide;
  trackingEvents: Array<TrackingEvent>;
  image?: ImageObject;
  link?: string;
  isPreMatch: boolean;
}

const PLACEHOLDER_IMAGE_URL =
  "https://images.onefootball.com/cw/icons/entity-logo-placeholder.svg";

export const MatchScoreTeam: FC<MatchScoreTeamProps> = (props) => {
  const { t } = useTranslation("web-payments");
  const ariaLabelId = useId();

  const { name, side, trackingEvents, image, link, isPreMatch } = props;

  const containerStyle = classnames(
    styles.container,
    side === TeamSide.TEAM_SIDE_HOME && styles.home,
    side === TeamSide.TEAM_SIDE_AWAY && styles.away,
    isPreMatch && styles.preMatch,
  );

  const titleStyle = classnames(styles.name, !isPreMatch && styles.titleStyle);

  const imageSource = image?.path ?? PLACEHOLDER_IMAGE_URL;
  const imageAlt =
    image?.alt ?? t("ENTITY_LOGO_PLACEHOLDER_ALT_TEXT") ?? "Logo";

  const onClick = () => {
    trackingService.sendXpaTracking(trackingEvents, EventType.EVENT_CLICK);
  };

  if (isNonEmptyString(link)) {
    return (
      <Link
        href={link}
        className={containerStyle}
        aria-labelledby={ariaLabelId}
        onClick={onClick}
      >
        <>
          <EntityLogo
            className={styles.icon}
            src={imageSource}
            alt={imageAlt}
            isPreloaded
            withHover
          />
          <span id={ariaLabelId} className={titleStyle}>
            {name}
          </span>
        </>
      </Link>
    );
  }

  return (
    <div className={containerStyle}>
      <EntityLogo
        className={styles.icon}
        src={imageSource}
        alt={imageAlt}
        isPreloaded
      />
      <span className={titleStyle}>{name}</span>
    </div>
  );
};
